import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { User } from '@data/models/user.interface';
import { RootState } from '@stores/models/store';
import { Roles, ScudRoles } from 'constants/roles';

const useUser = () => {
  const user: User = useSelector<RootState, User>(({ user }) => user);

  const hasPermission = useCallback(
    (permission: string): boolean => user.permissions.includes(permission),
    [],
  );

  const hasOneOfPermissions = useCallback(
    (...permissions: string[]): boolean =>
      permissions.some((permission: string) => hasPermission(permission)),
    [],
  );

  const mainUserRole = useMemo(() => {
    if (user.roles.includes(ScudRoles.NinaSuperAdmin)) {
      return Roles[ScudRoles.NinaSuperAdmin];
    }
    if (user.roles.includes(ScudRoles.NinaResponsableCG)) {
      return Roles[ScudRoles.NinaResponsableCG];
    }
    if (user.roles.includes(ScudRoles.NinaAssistantCG)) {
      return Roles[ScudRoles.NinaAssistantCG];
    }
    if (user.roles.includes(ScudRoles.NinaAgency)) {
      return Roles[ScudRoles.NinaAgency];
    }

    return '';
  }, [user.roles]);

  const isUserAssistantCG: boolean = useMemo(
    () => mainUserRole === Roles[ScudRoles.NinaAssistantCG],
    [user.roles],
  );

  const isUserRespCG: boolean = useMemo(
    () => mainUserRole === Roles[ScudRoles.NinaResponsableCG],
    [user.roles],
  );

  const isUserSuperAdmin: boolean = useMemo(
    () => mainUserRole === Roles[ScudRoles.NinaSuperAdmin],
    [user.roles],
  );

  /** True when the user only holds the fallback perimeter resolved from Lucie. */
  const isUserAgency: boolean = useMemo(
    () => user.roles.includes(ScudRoles.NinaAgency),
    [user.roles],
  );

  const isEmulated: boolean = useMemo(
    (): boolean => user.emulateBy !== '',
    [user.emulateBy],
  );

  return {
    user,
    hasPermission,
    hasOneOfPermissions,
    isUserAssistantCG,
    isUserRespCG,
    isUserSuperAdmin,
    isUserAgency,
    mainUserRole,
    isEmulated,
  };
};

export default useUser;
